import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSiteSettings, pick } from '@/contexts/SiteSettingsContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import useReveal from '@/hooks/useReveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  useReveal();
  const { t, lang } = useLanguage();
  const { settings } = useSiteSettings();
  const displayFont = lang === 'ar' ? 'var(--font-ar-display)' : 'var(--font-en-display)';
  const bodyFont = lang === 'ar' ? 'var(--font-ar-body)' : 'var(--font-en-body)';
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const isAr = lang === 'ar';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const phone = pick(settings?.phone, '+971 52 289 8249');
  const email = pick(settings?.email, 'info@gmreae.com');
  const address = pick(isAr ? settings?.address_ar : settings?.address_en, t('contact', 'address'));
  const hours = pick(isAr ? settings?.business_hours_ar : settings?.business_hours_en, isAr ? 'الأحد - السبت · 8:00 صباحاً - 8:00 مساءً' : 'Sunday – Saturday · 8:00 AM – 8:00 PM GST');

  const contactInfo = [
    { icon: Phone, label: isAr ? 'اتصل بنا' : 'Call Us', value: phone, sub: isAr ? 'الرد خلال ساعتين' : '2hr SLA response', href: `tel:${phone.replace(/\s/g, '')}` },
    { icon: Mail, label: isAr ? 'البريد الإلكتروني' : 'Email', value: email, sub: isAr ? 'الرد خلال ساعتين' : '2hr SLA response', href: `mailto:${email}` },
    { icon: MapPin, label: isAr ? 'العنوان' : 'Office', value: address, sub: isAr ? 'اجتماع افتراضي متاح' : 'Virtual meeting available' },
    { icon: Clock, label: isAr ? 'ساعات العمل' : 'Hours', value: hours, sub: isAr ? 'الخميس/الجمعة: بموعد مسبق' : 'Thu/Fri: by appointment' },
  ];

  return (
    <div data-testid="contact-page">
      <section className="bg-[#071E2C] pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(201,168,76,0.14),transparent_60%)] pointer-events-none" />
        <div className="absolute inset-x-6 lg:inset-x-12 top-24 h-px bg-gradient-to-r from-transparent via-[#A68966]/30 to-transparent" />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 text-center relative">
          <div className="gmre-overline justify-center mb-5 gmre-reveal">{lang === 'ar' ? 'نحن هنا لمساعدتك' : "Let's Talk · We're Here to Help"}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-semibold text-white mb-5 tracking-tight gmre-reveal gmre-reveal-delay-1" style={{ fontFamily: displayFont, lineHeight: 1.1, textWrap: 'balance' }}>
            {t('contact', 'title')}
          </h1>
          <div className="gmre-ornament mb-6 gmre-reveal gmre-reveal-delay-2"><div className="line" /><div className="dot" /><div className="line" /></div>
          <p className="text-white/65 max-w-2xl mx-auto text-base lg:text-lg leading-relaxed gmre-reveal gmre-reveal-delay-3" style={{ fontFamily: bodyFont, lineHeight: lang === 'ar' ? '2' : '1.75' }}>
            {t('contact', 'sub')}
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFB] py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-4">
              {contactInfo.map((item, i) => {
                const Wrapper = item.href ? 'a' : 'div';
                const wrapperProps = item.href ? { href: item.href } : {};
                return (
                  <Wrapper key={i} data-testid={`contact-info-${i}`} {...wrapperProps} className={`gmre-glass-light flex items-start gap-4 p-5 rounded-2xl group gmre-reveal gmre-reveal-delay-${Math.min(i + 1, 4)}`}>
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0B2F44] to-[#071E2C] flex items-center justify-center flex-shrink-0 group-hover:from-[#C9A84C] group-hover:to-[#A68966] gmre-ios-transition shadow-md shadow-[#0B2F44]/15">
                      <item.icon className="w-4 h-4 text-[#C9A84C] group-hover:text-[#0B2F44] transition-colors" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-[#A68966] tracking-[0.22em] uppercase mb-1">{item.label}</p>
                      <p className="font-semibold text-[#0B2F44] text-sm tracking-tight truncate">{item.value}</p>
                      <p className="text-[11px] text-[#4A6572] mt-1">{item.sub}</p>
                    </div>
                  </Wrapper>
                );
              })}
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="gmre-glass-light rounded-3xl p-8 lg:p-12 gmre-reveal gmre-reveal-delay-2">
                {status === 'success' ? (
                  <div data-testid="contact-success" className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 mx-auto mb-5 flex items-center justify-center">
                      <CheckCircle className="w-7 h-7 text-emerald-600" strokeWidth={1.5} />
                    </div>
                    <div className="gmre-overline justify-center mb-3" style={{ color: '#10B981' }}>{lang === 'ar' ? 'تم الإرسال' : 'Delivered'}</div>
                    <h3 className="text-2xl font-semibold text-[#0B2F44] mb-2 tracking-tight" style={{ fontFamily: displayFont }}>
                      {lang === 'ar' ? 'تم الإرسال بنجاح' : 'Message Sent'}
                    </h3>
                    <p className="text-[#4A6572] text-sm">{lang === 'ar' ? 'سنتواصل معك خلال ساعتين' : "We'll respond within 2 hours."}</p>
                    <Button onClick={() => setStatus('')} className="gmre-btn-sheen mt-7 bg-[#0B2F44] hover:bg-[#0D3A54] text-white tracking-[0.14em] uppercase text-xs px-6 py-5 rounded-none">
                      {lang === 'ar' ? 'إرسال رسالة أخرى' : 'Send Another'}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="gmre-overline mb-3">{lang === 'ar' ? 'تواصل مباشر' : 'Direct Inquiry'}</div>
                    <h2 className="text-2xl lg:text-3xl font-semibold text-[#0B2F44] mb-7 tracking-tight" style={{ fontFamily: displayFont }}>
                      {lang === 'ar' ? 'احكِ لنا عن مشروعك' : 'Tell us about your project'}
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="text-[10px] text-[#A68966] tracking-[0.2em] uppercase block mb-2">{t('contact', 'name')} *</label>
                          <Input
                            data-testid="contact-name-input"
                            value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })}
                            className="h-[52px] bg-white/70 border-[#D0D8DC] focus:border-[#A68966] focus:ring-[#A68966]/15 gmre-ios-transition"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#A68966] tracking-[0.2em] uppercase block mb-2">{t('contact', 'email')} *</label>
                          <Input
                            data-testid="contact-email-input"
                            type="email"
                            value={form.email}
                            onChange={e => setForm({ ...form, email: e.target.value })}
                            className="h-[52px] bg-white/70 border-[#D0D8DC] focus:border-[#A68966] focus:ring-[#A68966]/15 gmre-ios-transition"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="text-[10px] text-[#A68966] tracking-[0.2em] uppercase block mb-2">{t('contact', 'phone')}</label>
                          <Input
                            data-testid="contact-phone-input"
                            value={form.phone}
                            onChange={e => setForm({ ...form, phone: e.target.value })}
                            className="h-[52px] bg-white/70 border-[#D0D8DC] focus:border-[#A68966] focus:ring-[#A68966]/15 gmre-ios-transition"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#A68966] tracking-[0.2em] uppercase block mb-2">{t('contact', 'subject')} *</label>
                          <Input
                            data-testid="contact-subject-input"
                            value={form.subject}
                            onChange={e => setForm({ ...form, subject: e.target.value })}
                            className="h-[52px] bg-white/70 border-[#D0D8DC] focus:border-[#A68966] focus:ring-[#A68966]/15 gmre-ios-transition"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-[#A68966] tracking-[0.2em] uppercase block mb-2">{t('contact', 'message')} *</label>
                        <Textarea
                          data-testid="contact-message-input"
                          value={form.message}
                          onChange={e => setForm({ ...form, message: e.target.value })}
                          rows={5}
                          className="bg-white/70 border-[#D0D8DC] focus:border-[#A68966] focus:ring-[#A68966]/15 gmre-ios-transition"
                          required
                        />
                      </div>
                      <Button
                        type="submit"
                        data-testid="contact-submit-btn"
                        disabled={loading}
                        className="gmre-btn-sheen bg-[#C9A84C] hover:bg-[#A68966] text-[#0B2F44] font-semibold tracking-[0.14em] uppercase text-sm px-8 py-6 rounded-none shadow-[0_8px_24px_-8px_rgba(201,168,76,0.5)]"
                      >
                        <Send className="w-4 h-4 me-2" />
                        {loading ? (lang === 'ar' ? 'جاري الإرسال...' : 'Sending…') : t('contact', 'send')}
                      </Button>
                      {status === 'error' && (
                        <p className="text-[#C0392B] text-sm">{lang === 'ar' ? 'حدث خطأ. حاول مرة أخرى.' : 'An error occurred. Please try again.'}</p>
                      )}
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Embed */}
      <section data-testid="contact-map" className="bg-[#F8FAFB] pb-20 lg:pb-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="gmre-glass-light rounded-3xl overflow-hidden p-2">
            <iframe
              title="Guleed Mortgage Office Location"
              src={`https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`}
              width="100%"
              height="440"
              style={{ border: 0, borderRadius: 20 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
